import { useState } from "react"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import {
  REGION_OPTIONS,
  CENTER_OPTIONS,
  CATEGORY_OPTIONS,
  DEPARTMENT_OPTIONS,
} from "../../constants"

const findOpt = (opts, val) => opts.find((o) => o.value === val) || null

const fieldLabelSx = {
  letterSpacing: "0.04em",
  mb: 0.5,
  display: "block",
}

// Report-page-only filter toolbar. Presentational redesign only: it drives
// the exact same `values`/`onChange`/`onReset` contract as the shared
// ProcurementFilterBar (still backed by useProcurementFilters → URL params),
// so filter behavior/data source is unchanged — only the layout is new.
const ReportFilterToolbar = ({ values = {}, onChange, onReset }) => {
  const [showMore, setShowMore] = useState(false)
  const set = (patch) => onChange && onChange(patch)

  const centerOptions = values.region
    ? CENTER_OPTIONS.filter((c) => c.region === values.region)
    : CENTER_OPTIONS

  const activeCount = ["search", "region", "center", "category", "department", "from", "to"].filter(
    (k) => values[k]
  ).length

  return (
    <MDBox
      bgColor="white"
      borderRadius="xl"
      p={2}
      mb={3}
      sx={{ border: "1px solid", borderColor: "grey.200" }}
    >
      <MDBox display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
            Date Range
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDBox sx={{ width: { xs: "7.5rem", sm: "8.5rem" } }}>
              <MDInput
                fullWidth
                type="date"
                size="small"
                labelType="non-sticky"
                value={values.from || ""}
                onChange={(e) => set({ from: e.target.value })}
              />
            </MDBox>
            <MDTypography variant="caption" color="text">
              –
            </MDTypography>
            <MDBox sx={{ width: { xs: "7.5rem", sm: "8.5rem" } }}>
              <MDInput
                fullWidth
                type="date"
                size="small"
                labelType="non-sticky"
                value={values.to || ""}
                onChange={(e) => set({ to: e.target.value })}
              />
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox sx={{ width: { xs: "100%", sm: "9.5rem" } }}>
          <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
            Region
          </MDTypography>
          <MDSelect
            isClearable
            placeholder="All regions"
            options={REGION_OPTIONS}
            value={findOpt(REGION_OPTIONS, values.region)}
            onChange={(o) => set({ region: o?.value || "", center: "" })}
          />
        </MDBox>

        <MDBox sx={{ width: { xs: "100%", sm: "10.5rem" } }}>
          <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
            Center
          </MDTypography>
          <MDSelect
            isClearable
            placeholder="All centers"
            options={centerOptions}
            value={findOpt(CENTER_OPTIONS, values.center)}
            onChange={(o) => set({ center: o?.value || "" })}
          />
        </MDBox>

        <MDBox sx={{ width: { xs: "100%", sm: "10.5rem" } }}>
          <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
            Category
          </MDTypography>
          <MDSelect
            isClearable
            placeholder="All categories"
            options={CATEGORY_OPTIONS}
            value={findOpt(CATEGORY_OPTIONS, values.category)}
            onChange={(o) => set({ category: o?.value || "" })}
          />
        </MDBox>

        <MDBox sx={{ width: { xs: "100%", sm: "10.5rem" } }}>
          <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
            Department
          </MDTypography>
          <MDSelect
            isClearable
            placeholder="All departments"
            options={DEPARTMENT_OPTIONS}
            value={findOpt(DEPARTMENT_OPTIONS, values.department)}
            onChange={(o) => set({ department: o?.value || "" })}
          />
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1} sx={{ ml: { sm: "auto" } }}>
          <MDButton
            variant="text"
            color="info"
            size="small"
            onClick={() => setShowMore((s) => !s)}
          >
            <Icon fontSize="small" sx={{ mr: 0.5 }}>
              {showMore ? "expand_less" : "tune"}
            </Icon>
            More Filters
          </MDButton>
          <MDButton
            variant="text"
            color="secondary"
            size="small"
            disabled={activeCount === 0}
            onClick={onReset}
          >
            Clear all
          </MDButton>
        </MDBox>
      </MDBox>

      {showMore && (
        <MDBox
          mt={2}
          pt={2}
          sx={{ borderTop: "1px solid", borderColor: "grey.100" }}
        >
          <MDBox sx={{ width: { xs: "100%", sm: "16rem" } }}>
            <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" sx={fieldLabelSx}>
              Search
            </MDTypography>
            <MDInput
              fullWidth
              size="small"
              labelType="non-sticky"
              placeholder="Search…"
              value={values.search || ""}
              onChange={(e) => set({ search: e.target.value })}
            />
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  )
}

export default ReportFilterToolbar
